import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ProgressIndicator from '@components/ProgressIndicator';
import Spacer from '@components/Spacer';
import DocumentRow from '@components/UserWorkflowInfo/DocumentsAction/DocumentRow';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import * as ApiModels from '@typings/api-models';
import { Reorder } from 'framer-motion';
import CollapsibleArea from '@/components/CollapsibleArea';
import { Folder, FolderOpen } from '@phosphor-icons/react';
import pluralize from 'pluralize';
import CheckBox from '@/components/DesignLibrary/Atoms/CheckBox';
import Button from '@/components/DesignLibrary/Button';
import { nanoid } from 'nanoid';
import DotsSixVertical from '@components/Icons/DotsSixVertical';
import DocumentUploadButton from '@/components/Document/DocumentUploadButton';
import removeNullish from '@/helpers/removeNullish';
import s from './UserWorkflowDocumentsSelectorForm.module.scss';

export type SelectCompiledDocument =
  & Pick<ApiModels.UserWorkflowDocument, 'id' | 'rank' | 'templateReferenceId' | 'referenceType' | 'path'>
  & Parameters<typeof DocumentRow>[0]['compiledDocument']
  & {
    isSelected: boolean;
  };

type Props = {
  creatorId: string;
  isForPetitionPdfGeneration: boolean;
  userWorkflowId: string;
  onSubmit: (compiledDocuments: SelectCompiledDocument[]) => Promise<void>;
};

const SOCIAL_SECURITY_USER_WORKFLOW_DOCUMENT_TITLE = '121 - Statement About Your Social Security Numbers';

const UserWorkflowDocumentsSelectorForm: FC<Props> = ({ userWorkflowId, isForPetitionPdfGeneration, creatorId, onSubmit }) => {
  const [compiledDocuments, setCompiledDocuments] = useState<SelectCompiledDocument[]>([]);
  const [folderedDocuments, setFolderedDocuments] = useState<Record<string, SelectCompiledDocument[]>>({});
  const containerRef = useRef<HTMLUListElement>(null);

  const { fetchingState: fetchingCompiledDocuments, data: loadedCompiledDocumentsResponse, getData: getUserWorkflowDocuments } = useNoodleApi(
    tsClient.workflows.getUserWorkflowCompiledDocuments,
    { toastOnError: true },
  );

  const { getData: updateUserWorkflowDocuments } = useNoodleApi(tsClient.workflows.updateUserWorkflowCompiledDocuments, {
    toastOnError: true,
  });

  useEffect(() => {
    getUserWorkflowDocuments({ perPage: 1000, userWorkflowId });
  }, [userWorkflowId, getUserWorkflowDocuments]);

  useEffect(() => {
    if (loadedCompiledDocumentsResponse?.items) {
      setCompiledDocuments(
        loadedCompiledDocumentsResponse.items
          .filter(
            doc =>
              doc.document?.fileName
              || ((doc.templateReferenceType === 'manually-added' || doc.templateReferenceType === 'document-request-file' || doc.templateReferenceType === 'ai-bot')
                && creatorId),
          )
          .map((item) => ({
            ...item,
            isSelected: isForPetitionPdfGeneration
              ? item.path === 'Forms & Schedules' && item.integrationSlug !== 'pacer' && item.title !== SOCIAL_SECURITY_USER_WORKFLOW_DOCUMENT_TITLE
              : false,
          })),
      );
    } else {
      setCompiledDocuments([]);
    }
  }, [loadedCompiledDocumentsResponse, creatorId, isForPetitionPdfGeneration]);

  const selectedCount = useMemo(
    () => compiledDocuments.filter(doc => doc.isSelected).length,
    [compiledDocuments],
  );

  useEffect(() => {
    if (compiledDocuments) {

      const folderedDocs = compiledDocuments.reduce((acc: Record<string, SelectCompiledDocument[]>, doc) => {
        const folderName = doc.path?.split('/')[0] || 'Other files';
        if (!acc[folderName]) {
          acc[folderName] = [];
        }
        acc[folderName].push(doc);
        return acc;
      }, {});

      setFolderedDocuments(folderedDocs);
    }
  }, [compiledDocuments]);

  const refetchDocuments = useCallback(
    () => getUserWorkflowDocuments({ perPage: 1000, userWorkflowId }),
    [getUserWorkflowDocuments, userWorkflowId],
  );

  const handleReorderDocumentsInFolder = (reorderedDocs: SelectCompiledDocument[]): void => {
    setCompiledDocuments(prevDocs => {
      const reorderedIds = new Set(reorderedDocs.map(doc => doc.id));

      // Remove reordered docs from the original list
      const remainingDocs = prevDocs.filter(doc => !reorderedIds.has(doc.id));

      // Merge them back in place based on the first index they appeared
      const insertionIndex = prevDocs.findIndex(doc => reorderedIds.has(doc.id));

      const updatedDocs = [
        ...remainingDocs.slice(0, insertionIndex),
        ...reorderedDocs,
        ...remainingDocs.slice(insertionIndex),
      ];

      return updatedDocs;
    });
  };

  const handleNewManualUpload = useCallback(async (uploadedDocuments: Array<Pick<NonNullable<SelectCompiledDocument['document']>, 'id' | 'mimeType' | 'fileName' | 'fileSize'>> | null): Promise<void> => {
    const updates = compiledDocuments.map((doc, index) => ({
      ...doc,
      rank: index + 1,
    }))
      .map((doc, index) => {
        if (doc.document) {
          return {
            documentId: doc.document.id,
            id: doc.id,
            rank: index + 1,
            title: doc.title,
          };
        }
        if (!doc.document) {
          return {
            id: doc.id,
            rank: index + 1,
            title: doc.title,
          };
        }
        // Will never happen, just for typing purposes
        return {
          documentId: '',
          rank: 0,
          title: '',
        };
      })
      .filter(removeNullish);

    if (uploadedDocuments) {
      uploadedDocuments.forEach(uploadedDocument => {
        updates.push({
          documentId: uploadedDocument.id,
          rank: updates.length + 1,
          title: uploadedDocument.fileName,
        });
      });
    }

    await updateUserWorkflowDocuments({
      updates,
      userWorkflowId,
    });
    refetchDocuments();
  }, [compiledDocuments, updateUserWorkflowDocuments, refetchDocuments, userWorkflowId]);

  const handleSubmit = async (): Promise<void> => {
    onSubmit(compiledDocuments);
  };

  if (fetchingCompiledDocuments.isFetching) {
    return (
      <>
        <Spacer size={80} />
        <ProgressIndicator isPage isCentered />
        <Spacer size={80} />
      </>
    );
  }

  return (
    <>
      <div className={s['compiled-documents-content']}>
        <p className={s.consolidated}>{`${isForPetitionPdfGeneration ? `${selectedCount} items will be consolidated into the filing Petition.pdf` : 'The document you select will be added to PACER'}`}</p>
        <ul ref={containerRef} className={s.table}>
          {Object.entries(folderedDocuments).length === 0 ? <p className="body-sm" style={{ padding: 16 }}>No documents yet.</p> : <></>}
          {Object.entries(folderedDocuments).map(([folder, documents]) => (
            <CollapsibleArea
              Icon={{ Component: Folder, ComponentOpen: FolderOpen }}
              key={folder}
              title={folder}
              description={`${documents.length} ${pluralize('item', documents.length)}`}
              hideCaret
              className={s.folder}
            >
              {(folder === 'Forms & Schedules' || folder === 'Terms & Agreements' || folder === 'Credit Report Document')
                ? (
                  <Reorder.Group as="ul" values={documents} onReorder={handleReorderDocumentsInFolder} axis="y" layoutScroll>
                    {documents.map(doc => <Reorder.Item
                      value={doc}
                      key={doc.id}
                      as="li"
                      whileDrag={{ cursor: 'grabbing', zIndex: 10 }}
                      className={s.row}
                      layout="position"
                    >
                      <div className={s.formsItem}>
                        <DocumentRow
                          creatorId={creatorId}
                          compiledDocument={doc}
                          fileNameOnly
                          hideViewButton
                        />
                        <DotsSixVertical size={24} color="var(--color-gray-50)" />
                        <div className={s.vertBreak}></div>
                        {doc.document?.id && (
                          <CheckBox
                            id={`${doc.id}-select`}
                            isChecked={doc.isSelected}
                            label=''
                            onChange={() => {
                              setCompiledDocuments((prevDocs) => {
                                if (isForPetitionPdfGeneration) {
                                  // Allow multiple selection
                                  return prevDocs.map((d) =>
                                    d.id === doc.id ? { ...d, isSelected: !d.isSelected } : d,
                                  );
                                }
                                // Only allow one to be selected
                                return prevDocs.map((d) => ({
                                  ...d,
                                  isSelected: d.id === doc.id ? !d.isSelected : false,
                                }));
                              });
                            }}
                            className={s.checkbox}
                          />
                        )}
                      </div>
                    </Reorder.Item>)}
                  </Reorder.Group>
                )
                : (
                  <div className={s.subfolders}>
                    {Object.entries(
                      documents.reduce((acc: Record<string, SelectCompiledDocument[]>, doc) => {
                        const title = doc.title || 'Untitled';
                        if (!acc[title]) {
                          acc[title] = [];
                        }
                        acc[title].push(doc);
                        return acc;
                      }, {}),
                    ).map(([title, titledDocuments]) => (
                      <div key={title} className={s.subfolder}>
                        <CollapsibleArea
                          Icon={{ Component: Folder, ComponentOpen: FolderOpen }}
                          title={title}
                          description={`${titledDocuments.length} ${pluralize('item', titledDocuments.length)}`}
                          hideCaret
                        >
                          <Reorder.Group as="ul" values={titledDocuments} onReorder={handleReorderDocumentsInFolder} axis="y" layoutScroll>
                            {titledDocuments.map(doc => <Reorder.Item
                              value={doc}
                              key={doc.id}
                              as="li"
                              whileDrag={{ cursor: 'grabbing', zIndex: 10 }}
                              className={s.row}
                              layout="position"
                            >
                              <div>
                                <div style={{ minWidth: 70 }} />
                                <DocumentRow
                                  creatorId={creatorId}
                                  compiledDocument={doc}
                                  fileNameOnly
                                  hideViewButton
                                />
                                <DotsSixVertical size={24} color="var(--color-gray-50)" />
                                <div className={s.vertBreak}></div>
                                {doc.document?.id && (
                                  <CheckBox
                                    id={`${doc.id}-select`}
                                    isChecked={doc.isSelected}
                                    label=''
                                    onChange={() => {
                                      setCompiledDocuments((prevDocs) => {
                                        if (isForPetitionPdfGeneration) {
                                          // Allow multiple selection
                                          return prevDocs.map((d) =>
                                            d.id === doc.id ? { ...d, isSelected: !d.isSelected } : d,
                                          );
                                        }
                                        // Only allow one to be selected
                                        return prevDocs.map((d) => ({
                                          ...d,
                                          isSelected: d.id === doc.id ? !d.isSelected : false,
                                        }));
                                      });
                                    }}
                                    className={s.checkbox}
                                  />
                                )}
                              </div>
                            </Reorder.Item>)}
                          </Reorder.Group>
                        </CollapsibleArea>
                      </div>
                    ))}
                  </div>
                )}
            </CollapsibleArea>
          ))}
        </ul>
        <Spacer size={12}/>
        <DocumentUploadButton
          creatorId={creatorId}
          id={`${nanoid()}`}
          isInRow={true}
          onUploadComplete={d => handleNewManualUpload(d) }
          label={'Upload file'}
        />
        <Spacer size={12}/>
        <Button
          variant='primary'
          size='md'
          fullWidth
          onClick={handleSubmit}
          className={s.buttonSticky}
          disabled={selectedCount === 0}
        >
          {isForPetitionPdfGeneration ? 'Save selection & compile Petition.pdf' : 'Add selected document'}
        </Button>
      </div>
    </>
  );
};

export default UserWorkflowDocumentsSelectorForm;
